/**
 * The organization's allowances for the pages that only mention them (plan-014 E3b; FR12-SCR-07): the Dashboard's plan
 * card says "no minutes included on this plan" only where that is TRUE, and names the concurrency entitlement admission
 * enforces. Both come from E3a's allowances read, which is organization finance (`account.finance.read`): for anyone
 * else this answers `null` and the page says nothing it cannot know.
 */
import { useQuery } from "@tanstack/react-query";

import { useGateway } from "../../../api/provider.js";
import { organizationConcurrencyQuery } from "../../../api/query.js";
import type { EntitlementOverviewView } from "../../../api/slice/entitlement.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { entitlementKey } from "./allowance-screen.js";

/**
 * The organization's concurrency entitlement for any member (INT 2026-10-07): the workspace usage summary carries it, so
 * a Member sees the real organization limit — never the tier's alone. `null` while it is being read.
 */
export function useOrganizationConcurrency(): number | null {
  const gateway = useGateway();
  const read = useQuery(organizationConcurrencyQuery(gateway));
  return read.data === undefined ? null : read.data;
}

export function useOrganizationAllowance(): EntitlementOverviewView | null {
  const gateway = useGateway();
  const open = useOpenAccount();
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const read = useQuery({
    queryKey: entitlementKey(open.accountId ?? "none"),
    queryFn: () => gateway.readEntitlements(open.accountId ?? ""),
    enabled: open.accountId !== null && canRead,
    retry: false,
  });
  return read.data === undefined ? null : read.data;
}
